import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Package, ArrowRight, Folder } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DemoBanner from "../components/DemoBanner";

/**
 * NEW dealer-first landing (post-login).
 * Deliberately minimal: two big path cards (Buy / Sell) + a compact Pratiche
 * quick-access card. Advanced analytics widgets (KPI grid, IT↔DE trend chart,
 * carousels, watchlist, seller KPIs, AI Copilot promo) moved to /strumenti.
 * Nothing is deleted — everything stays reachable in ≤2 taps.
 */
export default function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pratiche, setPratiche] = useState(null);

  useEffect(() => {
    api
      .get("/pratiche")
      .then((r) => setPratiche(r.data || []))
      .catch(() => setPratiche([]));
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "";
  const openCount = (pratiche || []).filter((p) =>
    ["nuova", "in_lavorazione", "in_pratica", "confermata", "in_verifica"].includes(p.status)
  ).length;
  const toCompleteCount = (pratiche || []).filter(
    (p) => p.status === "in_pratica"
  ).length;

  return (
    <div className="py-6 md:py-10 space-y-8" data-testid="home-page">
      <DemoBanner testid="home-demo-banner" />

      {/* Tagline hero — spacious, quiet, on-brand */}
      <div className="pt-4 md:pt-8">
        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
          {firstName ? t("home.hi_dealer", { name: firstName }) : t("home.hi")}
        </p>
        <h1
          data-testid="home-hero-tagline"
          className="mt-3 font-heading font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-slate-950 max-w-3xl"
        >
          {t("home.hero_tagline_a")}
          <br />
          <span className="text-blue-600">{t("home.hero_tagline_b")}</span>
        </h1>
        <div
          data-testid="home-free-badge"
          className="mt-4 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1.5 text-[11px] md:text-xs font-medium text-emerald-800"
        >
          <span>{t("pricing_hint.free_join_full")}</span>
          <span aria-hidden className="text-emerald-300">·</span>
          <Link
            to="/tariffario"
            data-testid="home-free-badge-tariffario"
            className="font-semibold underline-offset-2 hover:underline"
          >
            {t("pricing_hint.see_tariffario")}
          </Link>
        </div>
      </div>

      {user && user.dealer_id == null && user.role !== "admin" && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="home-no-tenant-banner"
          className="rounded-2xl bg-amber-50 border border-amber-200 p-5 md:p-6"
        >
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
            {t("home.no_tenant_overline")}
          </div>
          <h2 className="mt-1 font-heading font-bold text-xl md:text-2xl tracking-tight text-slate-950">
            {t("pending_association.title")}
          </h2>
          <p className="mt-2 text-sm text-amber-900/80 max-w-2xl leading-relaxed">
            {t("pending_association.body")}
          </p>
          <Link
            to="/profilo"
            data-testid="home-no-tenant-cta"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-900 text-white px-4 h-9 text-xs font-semibold hover:bg-amber-950 transition-colors"
          >
            {t("pending_association.cta")}
          </Link>
        </motion.div>
      )}

      {/* Two large unmissable path cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="home-path-cards">
        <PathCard
          to="/marketplace"
          testid="home-path-buy"
          overline={t("home.buy_overline")}
          title={t("home.buy_title")}
          description={t("home.buy_desc")}
          Icon={ShoppingBag}
          variant="dark"
        />
        <PathCard
          to="/le-mie-auto"
          testid="home-path-sell"
          overline={t("home.sell_overline")}
          title={t("home.sell_title")}
          description={t("home.sell_desc")}
          Icon={Package}
          variant="light"
        />
      </div>

      {/* Compact pratiche quick-access card */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
        data-testid="home-pratiche-quickcard"
        className="rounded-2xl bg-white border border-slate-100 shadow-sm"
      >
        <Link
          to="/pratiche"
          data-testid="home-pratiche-link"
          className="flex items-center justify-between gap-4 p-5 md:p-6 hover:bg-slate-50/60 transition-colors rounded-2xl"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Folder className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                {t("home.pratiche_overline")}
              </div>
              <div
                data-testid="home-pratiche-summary"
                className="mt-0.5 font-heading font-semibold text-base md:text-lg tracking-tight text-slate-950"
              >
                {pratiche == null
                  ? t("home.pratiche_loading")
                  : t("home.pratiche_summary", {
                      open: openCount,
                      toComplete: toCompleteCount,
                    })}
              </div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
        </Link>
      </motion.div>
    </div>
  );
}

function PathCard({ to, testid, overline, title, description, Icon, variant }) {
  const dark = variant === "dark";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl p-6 md:p-8 min-h-[220px] flex flex-col justify-between transition-all border ${
        dark
          ? "bg-[#0A0A0B] text-white border-transparent hover:bg-[#111114]"
          : "bg-white text-slate-950 border-slate-100 shadow-sm hover:shadow-md"
      }`}
    >
      {dark && (
        <>
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-emerald-500/12 blur-3xl pointer-events-none" />
        </>
      )}
      <div className="relative z-10 flex items-start justify-between gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${dark ? "bg-white/10 text-blue-300" : "bg-blue-50 text-blue-600"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${dark ? "text-blue-300" : "text-slate-400"}`}
        >
          {overline}
        </span>
      </div>
      <div className="relative z-10">
        <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter leading-tight">
          {title}
        </h2>
        <p className={`mt-2 text-sm max-w-md leading-relaxed ${dark ? "text-slate-300" : "text-slate-500"}`}>
          {description}
        </p>
        <div className={`mt-6 inline-flex items-center gap-2 text-sm font-semibold ${dark ? "text-white" : "text-blue-600"}`}>
          <span className="border-b border-current pb-0.5 transition-all group-hover:pb-1">
            {dark ? "Sfoglia" : "Gestisci"}
          </span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}
